import { getPeriodToDate } from "./period";

// Extract Month value from period. Period should be in YYYYMM format
export const getMonthFromPeriod = (period: string) => {
  return Number(period.toString().substring(4, 6));
};

// Extract Year value from period. Period should be in YYYYMM format
export const getYearFromPeriod = (period: string) => {
  return Number(period.toString().substring(0, 4));
};

// Returns ISO date for given epoch time
export const unixEpochToISO = (unixTime: number): string => {
  const date = new Date(unixTime * 1000);
  const isoDate = date.toISOString();
  return isoDate;
};

// Returns ISO date for given epoch time in millisecond
export const unixEpochMilliSecToISO = (unixTime: number): string => {
  return new Date(unixTime).toISOString();
};

export const convertDateToYYYYMM = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is zero-based

  return `${year}${month}`;
};

/**
 * Formats a period string (YYYYMM) into a locale-aware year-month string using Intl.DateTimeFormat.
 * The output adapts to the user's locale for correct month/year ordering and separator.
 *
 * @param period "202402"
 * @param locale "ja-JP" | "en-US" | "ko-KR" | "de-DE" etc.
 * @returns locale-dependent string, e.g. "2024/02" (ja-JP), "02/2024" (en-US), "2024. 02." (ko-KR)
 */
export const getLocalizedPeriod = (period: string, locale: string): string => {
  if (!period || period.length !== 6) return "";
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  if (isNaN(year) || isNaN(month) || month < 1 || month > 12) return "";
  const date = new Date(year, month - 1, 15);
  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit" }).format(date);
};

/**
 * Derives the locale-specific date format pattern string using Intl.DateTimeFormat.formatToParts.
 * Uses a reference date (with a single-digit month) to detect:
 *   - token order and separators (including trailing literals)
 *   - whether the locale zero-pads the month (MM) or not (M)
 *
 * The backend receives this pattern and performs token replacement:
 *   - "YYYY" → 4-digit year
 *   - "MM"   → 2-digit zero-padded month
 *   - "M"    → month without leading zero
 *
 * @param locale "ja-JP" | "en-US" | "ko-KR" | "de-DE" etc.
 * @returns format pattern string, e.g. "YYYY/MM" (Japan), "MM/YYYY" (US), "YYYY. M." (ko-KR)
 *          Defaults to "MM-YYYY" if pattern cannot be determined.
 */
export const getDateFormatPattern = (locale: string): string => {
  try {
    // Use a single-digit month (February) to detect whether the locale zero-pads
    const referenceDate = new Date(2020, 1, 15); // Feb 2020
    const parts = new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit" }).formatToParts(referenceDate);
    // Check if the locale actually produces a leading zero for single-digit months
    const monthPart = parts.find((p) => p.type === "month");
    const isZeroPadded = monthPart?.value.startsWith("0") ?? true;

    const pattern = parts
      .map((part) => {
        if (part.type === "year") return "YYYY";
        if (part.type === "month") return isZeroPadded ? "MM" : "M";
        return part.value; // separator/literal characters
      })
      .join("");
    return pattern;
  } catch {
    return "MM-YYYY";
  }
};

// Formats a date string (e.g. from API) into a localized short date (e.g. "Jan 15, 2025")
export const formatDateKpi = (dateStr: string | undefined, locale: string = "en-US"): string => {
  if (!dateStr) return "--";
  try {
    const dateObject = new Date(`${dateStr.split("T")[0]}T00:00:00Z`);
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    return formatter.format(dateObject);
  } catch {
    return dateStr;
  }
};

// Formats a date string (e.g. from API) into a localized month and day, with no year (e.g. "September 12")
export const formatMonthDay = (dateStr: string | undefined, locale: string = "en-US"): string => {
  if (!dateStr) return "--";
  try {
    const dateObject = new Date(`${dateStr.split("T")[0]}T00:00:00Z`);
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
    return formatter.format(dateObject);
  } catch {
    return dateStr;
  }
};

export const formatDateLong = (dateStr: string | undefined, locale: string = "en-US"): string => {
  if (!dateStr) return "--";
  try {
    const dateObject = new Date(`${dateStr.split("T")[0]}T00:00:00Z`);
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    return formatter.format(dateObject);
  } catch {
    return dateStr;
  }
};

export const pinDate = (
  dateStr: string | undefined,
  locale: string = "en-US",
  monthFormat: "long" | "short" = "long",
): string => {
  if (!dateStr) return "--";

  const periodString = getPeriodToDate(`${dateStr}`.replace("-", "").toString()).toISOString();

  try {
    const dateObject = new Date(`${periodString.split("T")[0]}T00:00:00Z`);
    const formatter = new Intl.DateTimeFormat(locale, {
      month: monthFormat,
      year: "numeric",
      timeZone: "UTC",
    });
    return formatter.format(dateObject);
  } catch {
    return dateStr;
  }
};
